// src/pages/SupervisorDetailPage.tsx
import React from "react";
import SupervisorDetailVisitList from "./components/SupervisorDetailVisitsList";
import { Heading, Subheading } from "../../design/heading";
import { Badge } from "../../design/badge";
import { ChevronLeftIcon } from "@heroicons/react/16/solid";
import { Link } from "../../design/link";
import SupervisorDetailFileList from "./components/SupervisorDetailFilesList";
import SupervisorLanguagesCard from "./components/SupervisorLanguagesCard";
import SupervisorNotesCard from "./components/SupervisorNotesCard";
import {
  DescriptionDetails,
  DescriptionList,
  DescriptionTerm,
} from "../../design/description-list";
import EditSupervisorButton from "./components/EditSupervisorButton";
import LoadingView from "../../components/LoadingView";
import { Avatar } from "../../design/avatar";
import moment from "moment";
import DeactivateButton from "./components/DeactivateButton";
import ReactivateButton from "./components/ReactivateButton";
import ErrorView from "../../components/ErrorView";
import {
  SupervisorHeaderProps,
  SupervisorBasicInfoProps,
  SupervisorEmploymentInfoProps,
} from "./types";
import { useSupervisorDetail } from "./hooks/useSupervisorDetail";
import { Divider } from "../../design/divider";
import { getInitials } from "../../utils/string";

// Breadcrumb Navigation
const Breadcrumb: React.FC = () => (
  <div className="max-lg:hidden">
    <Link
      href="/supervisors"
      className="inline-flex items-center gap-2 transition-colors text-sm/6 text-zinc-500 dark:text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-300"
    >
      <ChevronLeftIcon className="size-4 fill-zinc-400 dark:fill-zinc-500" />
      Supervisors
    </Link>
  </div>
);

// Supervisor Header with Avatar and Status Badge
const SupervisorHeader: React.FC<SupervisorHeaderProps> = React.memo(({ supervisor }) => (
  <div className="mt-4 lg:mt-8">
    <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-center gap-4">
        <Avatar
          src={supervisor.avatarUrl}
          alt={`${supervisor.firstName} ${supervisor.lastName}`}
          className="w-14 h-14 rounded-full"
          initials={getInitials(`${supervisor.firstName} ${supervisor.lastName}`)}
        />
        <div>
          <Heading>
            {supervisor.firstName} {supervisor.lastName}
          </Heading>
          <div className="mt-1">
            <Badge color={supervisor.status === "inactive" ? "red" : "green"}>
              {supervisor.status === "inactive" ? "Inactive" : "Active"}
            </Badge>
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <EditSupervisorButton supervisor={supervisor} />
        {supervisor.status !== "inactive" ? (
          <DeactivateButton supervisor={supervisor} />
        ) : (
          <ReactivateButton supervisor={supervisor} />
        )}
      </div>
    </div>
  </div>
));

// Contact Information Card
const ContactInfoCard: React.FC<SupervisorBasicInfoProps> = React.memo(({ supervisor }) => (
  <div className="p-6 bg-white border rounded-xl shadow-sm dark:bg-zinc-900 border-zinc-200 dark:border-zinc-700">
    <Subheading>Contact Information</Subheading>
    <Divider className="my-4" />
    <DescriptionList>
      <DescriptionTerm>Phone Number</DescriptionTerm>
      <DescriptionDetails>{supervisor.phoneNumber || "Not provided"}</DescriptionDetails>

      <DescriptionTerm>Email</DescriptionTerm>
      <DescriptionDetails>{supervisor.email || "Not provided"}</DescriptionDetails>
    </DescriptionList>
  </div>
));

// Employment Information Card
const EmploymentInfoCard: React.FC<SupervisorEmploymentInfoProps> = React.memo(({ supervisor }) => {
  const hasEmploymentInfo = supervisor.dateOfEmployment || supervisor.inactivatedOn;

  return (
    <div className="p-6 bg-white border rounded-xl shadow-sm dark:bg-zinc-900 border-zinc-200 dark:border-zinc-700">
      <Subheading>Employment Information</Subheading>
      <Divider className="my-4" />
      {!hasEmploymentInfo ? (
        <p className="text-sm text-zinc-500 dark:text-zinc-400">
          No employment information recorded. Edit to add employment dates.
        </p>
      ) : (
        <DescriptionList>
          {supervisor.dateOfEmployment && (
            <>
              <DescriptionTerm>Date of Employment</DescriptionTerm>
              <DescriptionDetails>
                {moment(supervisor.dateOfEmployment.toDate()).format("MMMM Do, YYYY")}
              </DescriptionDetails>
            </>
          )}
          {supervisor.inactivatedOn && (
            <>
              <DescriptionTerm>Inactivated On</DescriptionTerm>
              <DescriptionDetails>
                {moment(supervisor.inactivatedOn.toDate()).format("MMMM Do, YYYY")}
              </DescriptionDetails>
            </>
          )}
        </DescriptionList>
      )}
    </div>
  );
});

const SupervisorDetailPage: React.FC = () => {
  const { selectedSupervisor, loading, error } = useSupervisorDetail();

  // Early returns for loading and error states
  if (loading.details) {
    return <LoadingView />;
  }

  if (error.details) {
    return <ErrorView error={error.details} />;
  }

  if (!selectedSupervisor) {
    return <ErrorView error={new Error("Supervisor not found")} />;
  }

  return (
    <div className="space-y-6">
      {/* Breadcrumb Navigation */}
      <Breadcrumb />

      {/* Supervisor Header with Status Badge */}
      <SupervisorHeader supervisor={selectedSupervisor} />

      {/* Contact & Employment Info - Two Column Layout on Desktop */}
      <div className="grid gap-6 lg:grid-cols-2">
        <ContactInfoCard supervisor={selectedSupervisor} />
        <EmploymentInfoCard supervisor={selectedSupervisor} />
      </div>

      {/* Languages & Notes - Two Column Layout on Desktop */}
      <div className="grid gap-6 lg:grid-cols-2">
        <SupervisorLanguagesCard supervisor={selectedSupervisor} />
        <SupervisorNotesCard supervisorId={selectedSupervisor.id} />
      </div>

      {/* Related Data Sections */}
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="p-6 bg-white border rounded-xl shadow-sm dark:bg-zinc-900 border-zinc-200 dark:border-zinc-700">
          <SupervisorDetailFileList supervisorId={selectedSupervisor.id} />
        </div>
        <div className="p-6 bg-white border rounded-xl shadow-sm dark:bg-zinc-900 border-zinc-200 dark:border-zinc-700">
          <SupervisorDetailVisitList supervisorId={selectedSupervisor.id} />
        </div>
      </div>
    </div>
  );
};

export default SupervisorDetailPage;
